import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { formatDuration } from "../../lib/format";
import { normalizeImageUrl } from "../../lib/imageUrl";
import PublicNav from "../../components/PublicNav";
import { Lock, ShieldCheck, CheckCircle, ArrowLeft } from "@phosphor-icons/react";

export default function PayPage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isSuccess = window.location.pathname.endsWith("/success");

  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [savingState, setSavingState] = useState(false);
  const [statusState, setStatusState] = useState({ status: "pending", co_guest_status: "pending" });
  const [waiver, setWaiver] = useState(false);
  const [dietary, setDietary] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    api.get(`/pay/${token}`).then(r => {
      setInfo(r.data);
      setWaiver(!!r.data.co_guest.waiver_accepted);
      setDietary(r.data.co_guest.dietary_notes || "");
      setPhone(r.data.co_guest.phone || "");
    }).catch(e => setError(e.response?.data?.detail || "Link not found"));
  }, [token]);

  useEffect(() => {
    if (!isSuccess || !sessionId) return;
    let cancelled = false;
    let count = 0;
    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/pay/${token}/status/${sessionId}`);
        setStatusState(r.data);
        if (r.data.status === "paid" || count >= 15) return;
      } catch {}
      count += 1;
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
  }, [isSuccess, sessionId, token]);

  const persist = async () => {
    setSavingState(true);
    try {
      await api.patch(`/pay/${token}`, { waiver_accepted: waiver, dietary_notes: dietary, phone });
    } finally { setSavingState(false); }
  };

  if (error) return <Wrap><h1 className="serif text-3xl text-ms-ink">{error}</h1><p className="text-ms-stone mt-3">Please ask the lead booker to resend your link.</p></Wrap>;
  if (!info) return <Wrap><p className="text-ms-stone">Loading…</p></Wrap>;

  const { co_guest, lead_name, tour, instance } = info;
  const dt = new Date(instance.starts_at);

  if (isSuccess) {
    return (
      <Wrap>
        {statusState.status === "paid" || co_guest.status === "paid" ? (
          <>
            <CheckCircle size={56} weight="duotone" className="text-ms-brass mx-auto" />
            <p className="label-eyebrow text-ms-brass mt-5 text-center">Confirmed</p>
            <h1 className="serif text-ms-ink text-4xl md:text-5xl mt-3 text-center leading-[0.95]">Your share is in.</h1>
            <p className="text-ms-stone text-center mt-4">Thanks {co_guest.name}. {lead_name} has been notified. We'll see you on {dt.toLocaleDateString(undefined, {weekday:'long', month:'long', day:'numeric'})}.</p>
          </>
        ) : (
          <>
            <h1 className="serif text-ms-ink text-3xl text-center">Confirming your payment…</h1>
            <p className="text-ms-stone text-center mt-3">Just a moment.</p>
          </>
        )}
      </Wrap>
    );
  }

  const startCheckout = async () => {
    setError("");
    if (!waiver) { setError("Please accept the waiver to continue."); return; }
    setLoading(true);
    try {
      // persist waiver/dietary/phone first
      await api.patch(`/pay/${token}`, { waiver_accepted: waiver, dietary_notes: dietary, phone });
      const r = await api.post(`/pay/${token}/checkout`, { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      setError(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <Wrap>
      {co_guest.status === "paid" ? (
        <>
          <CheckCircle size={56} weight="duotone" className="text-ms-brass mx-auto" />
          <h1 className="serif text-ms-ink text-3xl mt-5 text-center">Your share is already paid.</h1>
          <p className="text-ms-stone text-center mt-3">Thanks {co_guest.name}. See you on {dt.toLocaleDateString()}.</p>
        </>
      ) : (
        <>
          <p className="label-eyebrow text-ms-brass">Your share</p>
          <h1 className="serif text-ms-ink text-4xl md:text-5xl mt-3 leading-[1.05]">Hello {co_guest.name}.</h1>
          <p className="text-ms-stone mt-5 leading-[1.7]">
            <b>{lead_name}</b> reserved a Mystery Served experience for your party. The date is locked — your share is below.
          </p>

          <div className="bg-ms-ivory border border-ms-edge rounded-sm overflow-hidden mt-8">
            {tour.hero_image && <img src={normalizeImageUrl(tour.hero_image)} alt="" className="w-full aspect-[3/1] object-cover" />}
            <div className="p-6">
              <p className="serif text-2xl text-ms-ink">{tour.title}</p>
              <div className="mt-4 space-y-2 text-sm">
                <Row k="When" v={dt.toLocaleString(undefined, {weekday:'long', month:'long', day:'numeric', hour:'numeric', minute:'2-digit'})} />
                <Row k="Where" v={tour.meeting_location || "Shared by your host"} />
                <Row k="Duration" v={formatDuration(tour)} />
              </div>
              <div className="brass-line my-5" />
              <div className="flex items-center justify-between">
                <span className="label-eyebrow text-ms-stone text-[10px]">Your share</span>
                <span className="serif text-3xl text-ms-ink" data-testid="pay-share-amount">${co_guest.share_amount.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Per-guest waiver + dietary */}
          <div className="mt-8 bg-ms-ivory border border-ms-edge rounded-sm p-6">
            <p className="serif text-xl text-ms-ink">A few details before you pay</p>

            <div className="mt-5">
              <label className="block">
                <span className="label-eyebrow text-ms-stone text-[10px]">Phone (optional)</span>
                <input value={phone} onChange={e=>setPhone(e.target.value)} onBlur={persist} className="field-input mt-2" data-testid="pay-phone" />
              </label>
            </div>

            <div className="mt-5">
              <label className="block">
                <span className="label-eyebrow text-ms-stone text-[10px]">Dietary restrictions / allergies</span>
                <textarea rows={3} value={dietary} onChange={e=>setDietary(e.target.value)} onBlur={persist} className="field-input mt-2" placeholder="Vegetarian, gluten-free, allergies…" data-testid="pay-dietary" />
              </label>
            </div>

            <label className="flex items-start gap-3 cursor-pointer mt-5">
              <input type="checkbox" checked={waiver} onChange={e=>{ setWaiver(e.target.checked); }} onBlur={persist} className="mt-1 accent-ms-brass" data-testid="pay-waiver" />
              <span className="text-ms-ink/85 text-sm leading-[1.6]">I acknowledge the experience requires walking, includes food but not drinks, and I accept the cancellation policy.</span>
            </label>
            {savingState && <p className="text-xs text-ms-stone mt-2">Saving…</p>}
          </div>

          {error && <p className="mt-4 text-ms-danger text-sm" data-testid="pay-error">{error}</p>}
          <button onClick={startCheckout} disabled={loading || !waiver} className="mt-6 w-full py-4 bg-ms-ink text-ms-cream hover:bg-ms-brass rounded-full label-eyebrow text-xs transition-all disabled:opacity-50" data-testid="pay-share-btn">
            {loading ? "Redirecting…" : `Pay $${co_guest.share_amount.toFixed(2)} securely`}
          </button>
          <p className="mt-3 flex items-center justify-center gap-2 text-xs text-ms-stone"><Lock size={14} /> Secured by Stripe.</p>
          <p className="mt-2 flex items-center justify-center gap-2 text-xs text-ms-stone"><ShieldCheck size={14} /> Drinks purchased separately at venues.</p>
        </>
      )}
    </Wrap>
  );
}

function Wrap({ children }) {
  return (
    <div className="public-shell">
      <PublicNav />
      <section className="bg-ms-cream pt-32 pb-24">
        <div className="max-w-xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-ms-stone hover:text-ms-ink text-sm mb-6"><ArrowLeft size={14} /> Mystery Served</Link>
          {children}
        </div>
      </section>
    </div>
  );
}
function Row({ k, v }) {
  return <div className="flex items-start justify-between gap-4"><span className="text-ms-stone">{k}</span><span className="text-ms-ink text-right">{v}</span></div>;
}
